// App.js — ACES Cities Network
// "/" → LandingView (no autenticado) | Dashboard (autenticado)
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth, ROL } from './contexts/AuthContext';
import { GalardonesProvider } from './contexts/GalardonesContext';
import { LangProvider, useLang } from './i18n';
import { Layout } from './components/Layout';
import './theme.css';
import { useState, lazy, Suspense } from 'react';

// Auth (eager — needed immediately)
import LoginView from './views/LoginView';
import LandingView from './views/LandingView';

// Shared views (lazy)
const RegisterView       = lazy(() => import('./views/shared/RegisterView'));
const PerfilView         = lazy(() => import('./views/shared/PerfilView'));
const NotificacionesView = lazy(() => import('./views/NotificacionesView'));
const RecursosACESView   = lazy(() => import('./views/RecursosACESView'));

// Master views (lazy)
const DashboardView          = lazy(() => import('./views/DashboardView'));
const CandidaturasView       = lazy(() => import('./views/CandidaturasView'));
const CandidaturaDetalleView = lazy(() => import('./views/CandidaturaDetalleView'));
const EvaluadoresView        = lazy(() => import('./views/EvaluadoresView'));
const GalaView               = lazy(() => import('./views/GalaView'));
const TitularidadesView      = lazy(() => import('./views/TitularidadesView'));
const ConfigView             = lazy(() => import('./views/ConfigView'));
const ChatView               = lazy(() => import('./views/ChatView'));

// Role views (lazy)
const CiudadHome    = lazy(() => import('./views/ciudad/CiudadHome'));
const EvaluadorHome = lazy(() => import('./views/evaluador/EvaluadorHome'));

// ── Splash de carga ───────────────────────────────────────────────────────────
function Splash() {
  const { t } = useLang();
  return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',
                 minHeight:'100vh',background:'var(--bg-base)',flexDirection:'column',gap:16}}>
      <div style={{width:52,height:52,borderRadius:14,
                   background:'linear-gradient(135deg,var(--cyan) 0%,var(--blue) 100%)',
                   display:'flex',alignItems:'center',justifyContent:'center',
                   animation:'pulse 1.5s ease-in-out infinite'}}>
        <style>{`@keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.7;transform:scale(.95)}}`}</style>
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#050e1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      </div>
      <span style={{fontSize:13,color:'var(--text-secondary)'}}>{t('app_nombre')||'ACES Cities Network'}</span>
    </div>
  );
}

// ── Lazy fallback ─────────────────────────────────────────────────────────────
function LazyFallback() {
  return (
    <div style={{display:'flex',justifyContent:'center',alignItems:'center',height:200}}>
      <div style={{width:32,height:32,borderRadius:'50%',
                   border:'2px solid var(--border)',borderTopColor:'var(--cyan)',
                   animation:'spin 0.8s linear infinite'}} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ── Guard: redirige a /login si no autenticado ────────────────────────────────
function Guard({children}) {
  const {estado} = useAuth();
  if (estado==='cargando') return <Splash/>;
  if (estado==='noauth') return <Navigate to="/login" replace/>;
  return children;
}

// ── Switch de modo evaluador (solo para master con doble rol) ─────────────────
function ModoEvalSwitch({ modoEval, onToggle }) {
  const { t } = useLang();
  return (
    <div style={{
      display:'flex', alignItems:'center', gap:10,
      padding:'6px 14px', marginBottom:16,
      background: modoEval ? 'rgba(255,179,0,.08)' : 'rgba(0,207,200,.06)',
      border: `1px solid ${modoEval ? 'rgba(255,179,0,.25)' : 'rgba(0,207,200,.2)'}`,
      borderRadius:10,
    }}>
      <div style={{width:8,height:8,borderRadius:'50%',background:modoEval?'var(--amber)':'var(--cyan)'}}/> 
      <span style={{fontSize:12,color:modoEval?'var(--amber)':'var(--cyan)',flex:1,fontWeight:600}}>
        {modoEval?(t('modo_evaluador_activo')||'Modo evaluador activo'):(t('modo_master_activo')||'Vista Master ACES')}
      </span>
      <button onClick={onToggle} style={{
        padding:'5px 14px',borderRadius:7,
        background:modoEval?'var(--amber)':'var(--cyan)',
        color:'#050e1a',border:'none',cursor:'pointer',
        fontSize:11,fontWeight:800,fontFamily:'var(--font-display)',letterSpacing:'.03em',
      }}>
        {modoEval?(t('cambiar_a_master')||'<< Master'):(t('cambiar_a_eval')||'Modo evaluador >>')}
      </button>
    </div>
  );
}

// ── Rutas por rol ─────────────────────────────────────────────────────────────
function MasterRoutes({ modoEval, setModoEval }) {
  const toggle = () => setModoEval(v => !v);
  if (modoEval) {
    return (
      <Suspense fallback={<LazyFallback/>}>
        <Routes>
          <Route path="/*" element={<><ModoEvalSwitch modoEval={true} onToggle={toggle}/><EvaluadorHome modoMaster={true} onSalir={toggle}/></>}/>
          <Route path="/notificaciones" element={<NotificacionesView/>}/>
          <Route path="/perfil"         element={<PerfilView/>}/>
        </Routes>
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<LazyFallback/>}>
      <Routes>
        <Route path="/"                  element={<><ModoEvalSwitch modoEval={false} onToggle={toggle}/><DashboardView/></>}/>
        <Route path="/dashboard"         element={<><ModoEvalSwitch modoEval={false} onToggle={toggle}/><DashboardView/></>}/>
        <Route path="/candidaturas"      element={<CandidaturasView/>}/>
        <Route path="/candidaturas/:id"  element={<CandidaturaDetalleView/>}/>
        <Route path="/evaluadores"       element={<EvaluadoresView/>}/>
        <Route path="/gala"              element={<GalaView/>}/>
        <Route path="/titularidades"     element={<TitularidadesView/>}/>
        <Route path="/chat"              element={<ChatView/>}/>
        <Route path="/notificaciones"    element={<NotificacionesView/>}/>
        <Route path="/config"            element={<ConfigView/>}/>
        <Route path="/recursos"          element={<RecursosACESView/>}/>
        <Route path="/perfil"            element={<PerfilView/>}/>
        <Route path="*"                  element={<Navigate to="/" replace/>}/>
      </Routes>
    </Suspense>
  );
}

function RolRoutes() {
  const {rol, estado} = useAuth();
  const [modoEval, setModoEval] = useState(false);
  if (estado==='cargando') return <Splash/>;

  if (rol===ROL.MASTER) return (
    <Layout modoEval={modoEval} onToggleModo={() => setModoEval(v=>!v)}>
      <MasterRoutes modoEval={modoEval} setModoEval={setModoEval}/>
    </Layout>
  );
  if (rol===ROL.CIUDAD) return (
    <Layout>
      <Suspense fallback={<LazyFallback/>}>
        <Routes>
          <Route path="/ciudad"          element={<CiudadHome/>}/>
          <Route path="/notificaciones"  element={<NotificacionesView/>}/>
          <Route path="/perfil"          element={<PerfilView/>}/>
          <Route path="*"                element={<Navigate to="/ciudad" replace/>}/>
        </Routes>
      </Suspense>
    </Layout>
  );
  if (rol===ROL.EVAL) return (
    <Layout>
      <Suspense fallback={<LazyFallback/>}>
        <Routes>
          <Route path="/evaluador"       element={<EvaluadorHome/>}/>
          <Route path="/recursos"        element={<RecursosACESView/>}/>
          <Route path="/notificaciones"  element={<NotificacionesView/>}/>
          <Route path="/perfil"          element={<PerfilView/>}/>
          <Route path="*"                element={<Navigate to="/evaluador" replace/>}/>
        </Routes>
      </Suspense>
    </Layout>
  );
  // Usuario autenticado pero sin rol CN — mostrar acceso denegado
  return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',
                 minHeight:'100vh',background:'var(--bg-base)',flexDirection:'column',gap:20,padding:20}}>
      <div style={{width:52,height:52,borderRadius:14,
                   background:'rgba(255,179,0,0.15)',border:'1px solid rgba(255,179,0,0.3)',
                   display:'flex',alignItems:'center',justifyContent:'center'}}>
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#FFB300" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
      </div>
      <h2 style={{fontSize:18,fontWeight:700,fontFamily:'var(--font-display)',color:'var(--text)'}}>
        Acceso ACES Cities Network
      </h2>
      <p style={{fontSize:13,color:'var(--text-secondary)',textAlign:'center',maxWidth:360}}>
        Tu cuenta no tiene un rol asignado en Cities Network.
        Necesitas un codigo ACESCN para acceder.
      </p>
      <button onClick={async () => {
        const { getAuth, signOut } = await import('firebase/auth');
        await signOut(getAuth());
        window.location.href = '/login';
      }} style={{
        padding:'10px 24px',borderRadius:8,background:'rgba(0,207,200,0.1)',
        border:'1px solid rgba(0,207,200,0.3)',color:'var(--cyan)',
        fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'var(--font-display)'
      }}>
        Cerrar sesion e intentar con otra cuenta
      </button>
    </div>
  );
}

// ── Rutas principales ─────────────────────────────────────────────────────────
function AppRoutes() {
  const {estado, rol, registrando} = useAuth();
  const home = rol===ROL.CIUDAD?'/ciudad':rol===ROL.EVAL?'/evaluador':'/dashboard';

  return (
    <Routes>
      {/* LANDING — ruta raiz publica */}
      <Route path="/"
        element={
          estado==='cargando' ? <Splash/> :
          estado==='auth'     ? <Navigate to={home} replace/> :
                                <LandingView/>
        }
      />

      {/* Login y registro — solo si no autenticado */}
      <Route path="/login"    element={estado==='auth'?<Navigate to={home} replace/>:<LoginView/>}/>
      <Route path="/registro" element={(estado==='auth' && !registrando)?<Navigate to={home} replace/>:<Suspense fallback={<LazyFallback/>}><RegisterView/></Suspense>}/>

      {/* Privacidad — siempre publica */}
      <Route path="/privacidad" element={<PrivacidadView/>}/>
      <Route path="/privacy"    element={<PrivacidadView/>}/>

      {/* Resto de rutas — protegidas */}
      <Route path="/*" element={<Guard><RolRoutes/></Guard>}/>
    </Routes>
  );
}

// ── Vista privacidad inline ──────────────────────────────────────────────────
function PrivacidadView() {
  window.location.href = '/privacidad.html';
  return null;
}

// ── App root ──────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <BrowserRouter>
      <LangProvider>
        <GalardonesProvider>
          <AuthProvider>
            <AppRoutes/>
          </AuthProvider>
        </GalardonesProvider>
      </LangProvider>
    </BrowserRouter>
  );
}
